import { useState, ReactNode } from 'react';

export interface Tab {
  id: string;
  label: string;
  icon?: ReactNode;
  badge?: string | number;
}

interface TabGroupProps {
  tabs: Tab[];
  defaultTab?: string;
  onChange?: (tabId: string) => void;
  children: (activeTabId: string) => ReactNode;
  variant?: 'underline' | 'pills' | 'segment';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function TabGroup({
  tabs,
  defaultTab,
  onChange,
  children,
  variant = 'underline',
  size = 'md',
  className = '',
}: TabGroupProps) {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    onChange?.(tabId);
  };

  const sizeClasses = {
    sm: 'text-xs px-2.5 py-1.5',
    md: 'text-sm px-3 py-2',
    lg: 'text-base px-4 py-2.5',
  };

  const baseTabClass = `${sizeClasses[size]} font-medium transition-all duration-200 relative`;

  const getTabClass = (tabId: string) => {
    const isActive = activeTab === tabId;

    if (variant === 'underline') {
      return `${baseTabClass} ${
        isActive
          ? 'text-white'
          : 'text-text-secondary hover:text-text-secondary'
      }`;
    }

    if (variant === 'pills') {
      return `${baseTabClass} rounded-lg ${
        isActive
          ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
          : 'text-text-secondary hover:text-text-secondary hover:bg-surface-600/50'
      }`;
    }

    if (variant === 'segment') {
      return `${baseTabClass} flex-1 text-center ${
        isActive
          ? 'bg-surface-600 text-white shadow-sm'
          : 'text-text-secondary hover:text-text-secondary'
      }`;
    }

    return baseTabClass;
  };

  return (
    <div className={className}>
      {/* Tab List */}
      <div
        className={`flex items-center ${
          variant === 'segment'
            ? 'bg-surface-700 rounded-lg p-1 gap-1'
            : variant === 'pills'
              ? 'gap-2 mb-4'
              : 'border-b border-surface-600 gap-1 mb-4'
        }`}
        role="tablist"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={getTabClass(tab.id)}
          >
            <span className="flex items-center gap-2">
              {tab.icon}
              <span>{tab.label}</span>
              {tab.badge !== undefined && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id
                    ? 'bg-primary-500/30 text-primary-300'
                    : 'bg-surface-500 text-text-secondary'
                }`}>
                  {tab.badge}
                </span>
              )}
            </span>

            {/* Underline indicator */}
            {variant === 'underline' && activeTab === tab.id && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-400 rounded-full" />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div role="tabpanel" className="fade-in">
        {children(activeTab)}
      </div>
    </div>
  );
}

// Simpler uncontrolled tabs for inline use
interface SimpleTabsProps {
  tabs: Array<{ id: string; label: string }>;
  activeTab: string;
  onTabChange: (tabId: string) => void;
  variant?: 'underline' | 'pills';
  size?: 'sm' | 'md';
  className?: string;
}

export function SimpleTabs({
  tabs,
  activeTab,
  onTabChange,
  variant = 'underline',
  size = 'sm',
  className = '',
}: SimpleTabsProps) {
  const sizeClasses = {
    sm: 'text-xs px-2.5 py-1.5',
    md: 'text-sm px-3 py-2',
  };

  return (
    <div
      className={`flex items-center ${
        variant === 'underline'
          ? 'border-b border-surface-600 gap-1'
          : 'gap-2'
      } ${className}`}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`${sizeClasses[size]} font-medium transition-all relative ${
              variant === 'underline'
                ? isActive
                  ? 'text-white'
                  : 'text-text-secondary hover:text-text-secondary'
                : isActive
                  ? 'bg-primary-500/20 text-primary-400 rounded-md'
                  : 'text-text-secondary hover:text-text-secondary'
            }`}
          >
            {tab.label}
            {variant === 'underline' && isActive && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-400 rounded-full" />
            )}
          </button>
        );
      })}
    </div>
  );
}
